import React, { useState, useRef, ChangeEvent, KeyboardEvent } from "react";
import { InputContainer, OtpCodeInput } from "./styles";

interface Props {
  length: number;
  onChange: (value: string) => void;
  className?: string;
  disabled?: boolean;
}

const OtpInput: React.FC<Props> = ({
  length,
  onChange,
  className,
  disabled,
}) => {
  const [otp, setOtp] = useState(Array(length).fill(""));
  const inputRefs = useRef<Array<HTMLInputElement | null>>(
    Array(length).fill(null)
  );

  const generateKey = (index: number) => `otp-input-${index}`;

  const handleOtpUpdate = (newOtp: Array<string>) => {
    setOtp(newOtp);
    onChange(newOtp.join(""));
  };

  const handleChange = (index: number, value: string) => {
    if (value === "") {
      const newOtp = [...otp];

      newOtp[index] = "";
      handleOtpUpdate(newOtp);
      return;
    }

    const digitsArray = Array.from(value, String);
    let processedValue = digitsArray.find(
      (digit: string) => digit !== otp[index]
    );

    if (!processedValue) {
      processedValue = otp[index];
    }

    if (/^[0-9]$/g.test(processedValue as string)) {
      const newOtp = [...otp];

      newOtp[index] = processedValue;

      handleOtpUpdate(newOtp);

      if (index < otp.length - 1 && inputRefs.current[index + 1]) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (
    index: number,
    event: KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Backspace" && index > 0 && otp[index] === "") {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();

    let pasteData = event.clipboardData?.getData("text/plain");

    pasteData = pasteData.replace(/\s/g, "");
    pasteData = pasteData.slice(0, otp.length);

    if (pasteData && /^\d+$/.test(pasteData)) {
      const newOtp = [...otp];
      pasteData.split("").forEach((value, index) => {
        newOtp[index] = value;
      });

      handleOtpUpdate(newOtp);
    }
  };

  return (
    <InputContainer className={className}>
      {otp.map((digit, index) => {
        return (
          <OtpCodeInput
            key={generateKey(index)}
            type="number"
            maxLength={1}
            value={digit}
            onInput={(e: ChangeEvent<HTMLInputElement>) =>
              handleChange(index, e.target.value)
            }
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) =>
              handleKeyDown(index, e)
            }
            onPaste={handlePaste}
            ref={(input) => {
              inputRefs.current[index] = input;
            }}
            disabled={disabled}
          />
        );
      })}
    </InputContainer>
  );
};

export default OtpInput;
